import { Stack, useTheme } from "@mui/material";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { TAB_LINKS_HEIGHT } from "components/TabLinks";
import { WasteDateRangePicker } from "components/WasteDateRangePicker";

/**
 * Rendered height of the period bar: its 16px padding top and bottom, the
 * standard-variant range field (a label above a 40px input, 56px together), and
 * the 1px bottom border applied below. Exported so each tab's page surface can
 * sit below the tabs *and* this bar rather than running under them.
 */
export const PERIOD_BAR_HEIGHT = 89;

/** Everything above an Invoice Tracking tab's own content: the tabs and the period bar. */
export const INVOICE_TRACKING_HEADER_HEIGHT = TAB_LINKS_HEIGHT + PERIOD_BAR_HEIGHT;

type Props = {
  from?: string;
  through?: string;
  onChange: (next: { from?: string; through?: string }) => void;
  errorMessage?: string | null;
};

/**
 * The period every Invoice Tracking tab answers for, set once above the tabs.
 *
 * It sits in the layout rather than in a tab because it is not a filter on one
 * tab's rows: it decides which invoices, which charges and which scheduled
 * windows any of the three are talking about. Switching tabs therefore keeps
 * the reader on the same dataset instead of moving them to whatever range that
 * tab happened to remember.
 */
export function InvoiceTrackingDateFilter({ from, through, onChange, errorMessage }: Props) {
  const theme = useTheme();

  return (
    <SubNavAppBar
      sx={{
        py: 2,
        px: 3,
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
        // Sticks below the tabs rather than behind them.
        top: {
          xs: `calc(${theme.spacing(xsHeight)} + ${TAB_LINKS_HEIGHT}px)`,
          md: `calc(${theme.spacing(mdHeight)} + ${TAB_LINKS_HEIGHT}px)`,
        },
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
        <WasteDateRangePicker
          startDate={from}
          endDate={through}
          onAccept={(next) => onChange({ from: next.startDate, through: next.endDate })}
          startLabel="Period start"
          endLabel="Period end"
          errorMessage={errorMessage}
          showHelperText={false}
        />
      </Stack>
    </SubNavAppBar>
  );
}
